import React, { useState } from 'react';

function lineCanEdit(line) {
  return Array.isArray(line.item?.modifier_groups) && line.item.modifier_groups.length > 0;
}

// Right-hand cart panel. Displays current sale lines with per-line
// stepper controls and one big "Take payment" button pinned to the
// bottom. Tapping it opens the combined payment modal where the operator
// picks cash (with change calc) or Tyro EFTPOS. Purely presentational —
// all state lives on PosPage except the inline comment editor.
export default function PosCart({
  cart,
  total,
  pickupName = '',
  sundaySchool = false,
  onSundaySchoolChange,
  onIncrement,
  onDecrement,
  onRemove,
  onEditLine,
  onSetComment,
  onEditPickup,
  onTakePayment,
  onClearCart,
}) {
  const [commentingKey, setCommentingKey] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const isEmpty = cart.length === 0;
  const itemCount = cart.reduce((sum, l) => sum + l.quantity, 0);
  const hasCafe = cart.some((l) => l.item?.venue === 'cafe');
  const pickupLabel = pickupName.trim();

  const openComment = (line) => {
    setCommentingKey(line.lineKey);
    setCommentDraft(line.comment || '');
  };

  const saveComment = (lineKey) => {
    onSetComment?.(lineKey, commentDraft);
    setCommentingKey(null);
    setCommentDraft('');
  };

  const clearComment = (lineKey) => {
    onSetComment?.(lineKey, '');
    setCommentingKey(null);
    setCommentDraft('');
  };

  return (
    <aside className="pos-cart">
      <div className="pos-cart-header">
        <h2 className="pos-cart-title">Current Sale</h2>
        <div className="pos-cart-header-meta">
          <span className="pos-cart-count">
            {itemCount === 0 ? '—' : `${itemCount} item${itemCount === 1 ? '' : 's'}`}
          </span>
          {!isEmpty && (
            <button
              type="button"
              className="pos-cart-clear"
              onClick={onClearCart}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {hasCafe && (
        <button
          type="button"
          className="pos-cart-pickup-name pos-cart-pickup-name-btn"
          title="Edit pickup name"
          onClick={() => onEditPickup?.()}
        >
          {pickupLabel ? (
            <>
              Pickup: <strong>{pickupLabel}</strong>
              <span className="pos-cart-pickup-edit">Edit</span>
            </>
          ) : (
            <span className="pos-cart-pickup-empty">Tap to set pickup name</span>
          )}
        </button>
      )}

      {hasCafe && (
        <button
          type="button"
          className={`pos-cart-ss-toggle${sundaySchool ? ' is-on' : ''}`}
          onClick={() => onSundaySchoolChange?.(!sundaySchool)}
          aria-pressed={sundaySchool}
          title="Marks cafe stickers with the Sunday School banner"
        >
          <span className="pos-cart-ss-check" aria-hidden>
            {sundaySchool ? '✓' : ''}
          </span>
          <span className="pos-cart-ss-label">Sunday School</span>
        </button>
      )}

      <div className="pos-cart-lines">
        {isEmpty ? (
          <div className="pos-cart-empty">Tap a tile to start a sale.</div>
        ) : (
          cart.map((line) => {
            const lineTotal = line.unitPrice * line.quantity;
            const canEdit = lineCanEdit(line);
            const editingComment = commentingKey === line.lineKey;
            return (
              <div key={line.lineKey} className="pos-cart-line">
                <div className="pos-cart-line-top">
                  <div className="pos-cart-line-name" title={line.item.name}>
                    {line.item.name}
                  </div>
                  <div className="pos-cart-line-total">
                    ${lineTotal.toFixed(2)}
                  </div>
                </div>
                {line.optionsLabel && (
                  <div className="pos-cart-line-options">{line.optionsLabel}</div>
                )}
                {editingComment ? (
                  <form
                    className="pos-cart-comment-edit"
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveComment(line.lineKey);
                    }}
                  >
                    <input
                      className="pos-cart-comment-input"
                      type="text"
                      maxLength={80}
                      value={commentDraft}
                      onChange={(e) => setCommentDraft(e.target.value)}
                      placeholder="e.g. no olives"
                      autoFocus
                    />
                    <div className="pos-cart-comment-actions">
                      <button type="submit" className="pos-cart-line-edit">Save</button>
                      {line.comment ? (
                        <button
                          type="button"
                          className="pos-cart-line-remove"
                          onClick={() => clearComment(line.lineKey)}
                        >
                          Clear
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="pos-cart-line-remove"
                        onClick={() => {
                          setCommentingKey(null);
                          setCommentDraft('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : line.comment ? (
                  <button
                    type="button"
                    className="pos-cart-line-comment"
                    onClick={() => openComment(line)}
                  >
                    {line.comment}
                    <span>Edit</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pos-cart-add-comment"
                    onClick={() => openComment(line)}
                  >
                    Add comment
                  </button>
                )}
                <div className="pos-cart-line-controls">
                  <button
                    type="button"
                    className="pos-cart-qty-btn"
                    onClick={() => onDecrement(line.lineKey)}
                    aria-label="Decrease"
                  >
                    &minus;
                  </button>
                  <span className="pos-cart-qty-value">{line.quantity}</span>
                  <button
                    type="button"
                    className="pos-cart-qty-btn"
                    onClick={() => onIncrement(line.lineKey)}
                    aria-label="Increase"
                  >
                    +
                  </button>
                  <div className="pos-cart-line-actions">
                    {canEdit && (
                      <button
                        type="button"
                        className="pos-cart-line-edit"
                        onClick={() => onEditLine?.(line)}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="pos-cart-line-remove"
                      onClick={() => onRemove(line.lineKey)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="pos-cart-footer">
        <div className="pos-cart-total-row">
          <span className="pos-cart-total-label">TOTAL</span>
          <span>${total.toFixed(2)}</span>
        </div>
        <div className="pos-cart-buttons">
          <button
            type="button"
            className="pos-pay-btn pos-pay-btn-take"
            onClick={onTakePayment}
            disabled={isEmpty}
          >
            <span className="pos-pay-btn-icon" aria-hidden>&#128179;</span>
            <span>TAKE PAYMENT</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
